import { Box, Button, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { FC, useEffect, useState } from "react";
import { ApprasialProfileUI } from "../AppraisalUI";
import { ButtonText } from "../UIElements/button";
import useResponsive from "../../hooks/useResponsive";
import {
  TimeRequestButton,
  TimesheetPayroll,
  TimesheetTimeOff,
} from "../TimesheetUI";
import DenseTimesheetTable from "./TableData";
import HistoryTable from "./historyTable";
import {
  TimesheetDetailsProps,
  TimesheetMedicContentProps,
  TimesheetMedicContentPropsDummyData,
} from "../../interface/timesheet";
import {
  formatFullName,
  formatTextType,
  limitToSeven,
} from "../../utils/url-id";
import { TimesheetEmployeeModalTable } from "./TimesheetModal";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  apiGetAllMedicTimesheetById,
  apiResoleFlaggedTimeSheet,
} from "../../api/timesheet";
import { useNotificationService } from "../notification";
import { useAuth } from "../../hooks/useAuth";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";

const TimesheetDetailsContent: FC<{
  timesheetInfo: TimesheetDetailsProps;
  loading: boolean;
}> = ({ loading, timesheetInfo }) => {
  const theme = useTheme();

  const [data, setData] = useState({
    showCompare: false,
    result: false,
  });
  const [openModal, setOpenModal] = useState(false);
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { showNotification } = useNotificationService();
  const { token } = useAuth();

  const [medic_timesheet, setMedic_timesheet] = useState<
    TimesheetMedicContentProps[]
  >([TimesheetMedicContentPropsDummyData]);

  // set up pagination state
  const [pagination_state, setPagination_state] = useState<PaginationDetails>(
    PaginationDetailsDummyData
  );

  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };
  useEffect(() => {
    if (!loading && !!timesheetInfo.medic) {
      const medicId =
        timesheetInfo?.medic?.length > 5 ? timesheetInfo.medic : "";
      const fields = { medicId, token };
      mutation.mutate(fields);
    }
  }, [pagination_state?.page, loading]);

  const mutation = useMutation({
    mutationFn: (fields: { token: string; medicId: string }) =>
      apiGetAllMedicTimesheetById(fields),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (Array.isArray(data?.data?.data)) {
          setMedic_timesheet(data.data.data);
        } else {
          setMedic_timesheet(data.data.data);
        }
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });
  const handleOpenModal = () => setOpenModal(!openModal);

  const resolveMutation = useMutation({
    mutationFn: (fields: { timesheetId: string; token: string }) =>
      apiResoleFlaggedTimeSheet(fields),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        // showNotification(data?.data?.message ?? "", "success");
        queryClient.invalidateQueries({ queryKey: ["facilityTimesheet"] });

        navigate(-1);
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });

  return (
    <Box
      sx={{
        margin: 3,
        padding: isMedium ? 3 : 4,
        borderRadius: "3px",
        minHeight: "100%",
        backgroundColor: theme.palette.common.white,
      }}
    >
      <TimesheetEmployeeModalTable
        open={openModal}
        medicData={medic_timesheet}
        modalHandler={handleOpenModal}
        paginationState={pagination_state}
        setPaginationState={setPagination_state}
      />
      <Typography sx={{ ...headerSty, my: 0, mb: 2 }} variant="subtitle2">
        Employee Appraisal Details
      </Typography>
      {!loading ? (
        <ApprasialProfileUI
          status={formatTextType(timesheetInfo?.status) ?? "-"}
          name={formatFullName(
            timesheetInfo?.medic_info?.first_name,
            timesheetInfo?.medic_info?.last_name
          )}
          email={timesheetInfo?.medic_info?.email ?? "-"}
          staffId={limitToSeven(timesheetInfo?._id)}
          flagReason={timesheetInfo?.flag_note}
          shiftId={timesheetInfo?.shift}
        />
      ) : (
        <Skeleton variant="rounded" width={"100%"} height={100} />
      )}
      <Box sx={{ my: 2 }}>
        {timesheetInfo?.status === "flagged" && (
          <LoadingButton
            onClick={() => {
              resolveMutation.mutate({
                timesheetId: timesheetInfo._id,
                token,
              });
            }}
            loading={resolveMutation.isPending}
            variant="contained"
            sx={{ width: 190 }}
          >
            Resolve
          </LoadingButton>
        )}
      </Box>
      {/* <Stack
        direction="row"
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{ my: 3 }}
      >
        <Typography sx={{ ...headerSty }} variant="subtitle2">
          Timesheet History
        </Typography>
        <Button
          onClick={handleOpenModal}
          sx={{
            fontWeight: 700,
          }}
        >
          View all
        </Button>
      </Stack>
      {!loading ? (
        <Box
          sx={{
            background: "#FAFBFC",
            p: 2,
            mb: 2,
          }}
        >
          <HistoryTable historyList={medic_timesheet?.slice(0, 5)} loading={false} />
        </Box>
      ) : (
        <Skeleton variant="rounded" width={"100%"} height={300} />
      )} */}
      {/* <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ my: 2 }}
      >
        <Typography sx={headerSty} variant="subtitle2">
          Leave and Time Off
        </Typography>
        <ButtonText
          sx={{
            fontWeight: 700,
          }}
          onclick={() =>
            setData((prevState) => ({ ...prevState, showCompare: true }))
          }
        >
          Compare
        </ButtonText>
      </Stack>
      <TimesheetTimeOff
        startDate={"2nd Aug, 2024"}
        endDate={"22nd Aug, 2024"}
        requestType={"Time off"}
        duration={"3 weeks"}
        availableTime={"4 weeks"}
      />
      <TimeRequestButton /> */}

      {/* <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Typography sx={headerSty} variant="subtitle2">
          Overtime Analysis
        </Typography>
        <Stack direction="row">
          <ButtonText
            sx={{
              fontWeight: 700,
            }}
            onclick={() => { }}
          >
            View All
          </ButtonText>
        </Stack>
      </Stack>
      <Box sx={{ width: "100%" }}>
        <DenseTimesheetTable />
      </Box> */}

      {/* <Stack
        direction={isMedium ? "column" : "row"}
        alignItems={isMedium ? "start" : "center"}
        justifyContent={"space-between"}
      >
        <Typography sx={headerSty} variant="subtitle2">
          Payroll Integration
        </Typography>
        <Stack direction="row">
          <ButtonText
            sx={{
              fontWeight: 700,
            }}
            onclick={() => { }}
          >
            Add Comment
          </ButtonText>
          <ButtonText
            sx={{
              fontWeight: 700,
            }}
            onclick={() => { }}
          >
            View All
          </ButtonText>
        </Stack>
      </Stack> */}
      {/* <TimesheetPayroll /> */}
    </Box>
  );
};

export default TimesheetDetailsContent;
